import * as React from 'react';
import { useMemo, useState } from 'react';
import { Avatar } from '../../primitives/Avatar/Avatar';
import { Badge } from '../../primitives/Badge/Badge';
import { GovernanceTag } from '../GovernanceTag/GovernanceTag';
import { segmentMentions, type MentionMatcher } from '../Composer/mention';
import { mentionChip as mentionChipCls } from '../Composer/mention.css';
import { TrustBadge } from '../TrustBadge/TrustBadge';
import type { Message } from '../_types';
import {
  row as rowCls,
  avatarHidden as avatarHiddenCls,
  column as columnCls,
  metaLine as metaLineCls,
  authorName as authorNameCls,
  authorNameButton as authorNameButtonCls,
  authorRole as authorRoleCls,
  timestamp as timestampCls,
  separator as separatorCls,
  bubble as bubbleCls,
  bubbleScope as bubbleScopeCls,
  watermark as watermarkCls,
  paragraph as paragraphCls,
  list as listCls,
  systemLine as systemLineCls,
  systemTimestamp as systemTimestampCls,
} from './MessageBubble.css';

export type MessageBubbleDensity = 'comfortable' | 'compact';

export interface MessageBubbleProps extends Omit<React.HTMLAttributes<HTMLElement>, 'children'> {
  message: Message;
  density?: MessageBubbleDensity;
  isSelf?: boolean;
  /**
   * `false` renders a continuation row: no name line and an invisible avatar
   * (it keeps its width so the bubble stays in line with the one above).
   * Pass it for the second and later consecutive messages from one sender.
   */
  showMeta?: boolean;
  /** Override avatar background — for tenant-tinted human avatars. */
  avatarBg?: string;
  /**
   * Mention triggers to render as chips in the message text — the same
   * matchers the Composer typeahead is configured with, so `@Engineer` and
   * `/prd.md` look the same after sending as they did while typing. A token
   * whose `isMention` says no stays plain text. Omitted: no chips.
   */
  mentions?: readonly MentionMatcher[];
  onOpenProfile?: (memberId: string) => void;
  /** React 19 ref-as-prop. */
  ref?: React.Ref<HTMLElement>;
}

/** Map message variant → avatar ring tone. */
function ringToneFor(variant: Message['variant']): 'none' | 'blue' | 'purple' | 'teal' {
  if (variant === 'agent') return 'blue';
  if (variant === 'smith' || variant === 'mira' || variant === 'customAgent') return 'purple';
  if (variant === 'workstreamAgent') return 'teal';
  return 'none';
}

/** Map message variant → "Agent" badge tone in the metaLine. */
function showsAgentBadge(variant: Message['variant']): boolean {
  return variant === 'agent' || variant === 'smith' || variant === 'mira';
}

/**
 * One line of message text: committed mentions as chips, markdown in between.
 *
 * A mention run is never markdown-parsed — a filename like
 * `/report_v2_final.md` is full of `_`, which the inline pass would read as
 * emphasis and eat. But markdown is parsed over the WHOLE line, not per
 * segment: an emphasis or code span that merely CONTAINS a mention
 * (`*ask @Engineer to confirm*`, `` `read /prd.md` ``) still pairs its
 * delimiters, where segmenting first left them stranded as literal `*` and
 * backticks in every agent reply. Such a span renders as markdown with its
 * mention plain — the chip is what yields, not the formatting.
 */
function renderLine(text: string, mentions: readonly MentionMatcher[]): React.ReactNode {
  if (mentions.length === 0) return renderInlineMarkdown(text);
  const segments = segmentMentions(text, mentions);
  // Character offsets covered by a mention, so the markdown pass can tell a
  // span that contains one (keep, parse) from one that straddles a boundary
  // (drop, since its partner delimiter is inside the mention's own text).
  const spans: Array<[number, number]> = [];
  let at = 0;
  for (const seg of segments) {
    if (seg.mention) spans.push([at, at + seg.text.length]);
    at += seg.text.length;
  }
  return renderInlineMarkdown(text, spans);
}

/**
 * Simple inline markdown renderer — bold, italic, inline code.
 *
 * `mentionSpans` are half-open `[start, end)` offsets of committed mentions.
 * Text inside one is emitted as a chip; a markdown match that only PARTLY
 * overlaps one is not a match at all (its delimiter belongs to the filename,
 * as in `/report_v2_final.md`) and is passed through as plain text.
 */
function renderInlineMarkdown(
  text: string,
  mentionSpans: ReadonlyArray<readonly [number, number]> = [],
): React.ReactNode {
  const tokens: React.ReactNode[] = [];
  let key = 0;
  const push = (node: React.ReactNode) =>
    tokens.push(<React.Fragment key={key++}>{node}</React.Fragment>);
  /** Plain text plus any chips falling inside `[from, to)`. */
  const pushText = (from: number, to: number) => {
    let cursor = from;
    for (const [start, end] of mentionSpans) {
      if (start < cursor || end > to) continue;
      if (start > cursor) push(text.slice(cursor, start));
      push(<span className={mentionChipCls}>{text.slice(start, end)}</span>);
      cursor = end;
    }
    if (cursor < to) push(text.slice(cursor, to));
  };
  const re = /(\*\*[^*]+\*\*|\*[^*]+\*|_[^_]+_|`[^`]+`)/g;
  let lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text))) {
    const from = m.index;
    const to = from + m[0].length;
    // Straddles a mention: the delimiter is part of the filename, not markup.
    const straddles = mentionSpans.some(
      ([start, end]) => (from > start && from < end) || (to > start && to < end),
    );
    if (straddles) continue;
    if (from > lastIndex) pushText(lastIndex, from);
    const t = m[0];
    if (t.startsWith('**') && t.endsWith('**')) {
      push(<strong>{t.slice(2, -2)}</strong>);
    } else if (t.startsWith('*') && t.endsWith('*')) {
      push(<em>{t.slice(1, -1)}</em>);
    } else if (t.startsWith('_') && t.endsWith('_')) {
      push(<em>{t.slice(1, -1)}</em>);
    } else if (t.startsWith('`') && t.endsWith('`')) {
      push(<code>{t.slice(1, -1)}</code>);
    }
    lastIndex = m.index + t.length;
  }
  if (lastIndex < text.length) pushText(lastIndex, text.length);
  return tokens;
}

const bulletRe = /^\s*[-*]\s+/;

/**
 * Block-level markdown: blank-line-separated paragraphs, `- `/`* ` runs
 * as unordered lists, single newlines inside a paragraph as `<br>`.
 * Inline tokens (bold/italic/code) are delegated per-line to
 * `renderInlineMarkdown`. This is what makes agent replies keep their
 * line structure — the bubble div collapses raw whitespace, so newlines
 * must be turned into real block/`<br>` elements to survive.
 */
function renderMarkdown(text: string, mentions: readonly MentionMatcher[]): React.ReactNode {
  const lines = text.split('\n');
  const blocks: React.ReactNode[] = [];
  let key = 0;
  let i = 0;
  while (i < lines.length) {
    const line = lines[i] ?? '';
    if (line.trim() === '') {
      i++;
      continue;
    }
    if (bulletRe.test(line)) {
      const items: React.ReactNode[] = [];
      for (
        let l = lines[i];
        i < lines.length && l !== undefined && bulletRe.test(l);
        l = lines[++i]
      ) {
        items.push(<li key={key++}>{renderLine(l.replace(bulletRe, ''), mentions)}</li>);
      }
      blocks.push(
        <ul key={key++} className={listCls}>
          {items}
        </ul>,
      );
    } else {
      const para: React.ReactNode[] = [];
      for (
        let l = lines[i];
        i < lines.length && l !== undefined && l.trim() !== '' && !bulletRe.test(l);
        l = lines[++i]
      ) {
        if (para.length > 0) para.push(<br key={key++} />);
        para.push(<React.Fragment key={key++}>{renderLine(l, mentions)}</React.Fragment>);
      }
      blocks.push(
        <p key={key++} className={paragraphCls}>
          {para}
        </p>,
      );
    }
  }
  return blocks;
}

/**
 * MessageBubble — a single chat row in the prototype layout: avatar
 * (size lg) on the left, header (name + agent badge) above
 * a bubble with a 3px sender-coded border on the avatar side. The bubble's interior
 * uses asymmetric padding (24px right vs 14px left) to optically
 * balance the coloured stripe and a `calc(100% - 24px)` max-width so
 * it never stretches edge-to-edge inside the chat column.
 *
 * Validated 2026-05-02 against the BG Private workspace prototype at
 * `http://localhost:8000/`.
 */
export function MessageBubble({
  message,
  density = 'comfortable',
  isSelf = false,
  showMeta = true,
  avatarBg,
  mentions,
  onOpenProfile,
  ref,
  className,
  ...rest
}: MessageBubbleProps) {
  const variant = message.variant;
  const isSystem = variant === 'workstreamAgent';
  const isAgent = showsAgentBadge(variant);

  // The time is hidden until the bubble is clicked (WhatsApp-style); the
  // article's aria-label still carries it for assistive tech.
  const [showTime, setShowTime] = useState(false);

  const content = useMemo(
    () => renderMarkdown(message.text, mentions ?? []),
    [message.text, mentions],
  );

  if (isSystem) {
    const cls = [rowCls({ density, isSelf: false, workstreamAgent: true }), className]
      .filter(Boolean)
      .join(' ');
    return (
      <article
        ref={ref}
        role="status"
        aria-label={`System: ${message.text}`}
        className={cls}
        {...rest}
      >
        <div className={systemLineCls}>
          <span>{message.text}</span>
          <span className={systemTimestampCls}>· {message.time}</span>
        </div>
      </article>
    );
  }

  const cls = [rowCls({ density, isSelf, workstreamAgent: false }), className]
    .filter(Boolean)
    .join(' ');
  const bubbleClassName = [bubbleCls({ density, isSelf, variant }), bubbleScopeCls]
    .filter(Boolean)
    .join(' ');

  return (
    <article
      ref={ref}
      className={cls}
      aria-label={`${message.memberName} at ${message.time}`}
      {...rest}
    >
      <Avatar
        name={message.memberName}
        size="lg"
        className={showMeta ? undefined : avatarHiddenCls}
        ringTone={ringToneFor(variant)}
        style={avatarBg ? { background: avatarBg } : undefined}
      />
      <div className={columnCls({ isSelf })}>
        {showMeta && (
          <div className={metaLineCls({ isSelf })}>
            {onOpenProfile ? (
              <button
                type="button"
                className={authorNameButtonCls}
                onClick={() => onOpenProfile(message.memberId)}
              >
                {message.memberName}
              </button>
            ) : (
              <span className={authorNameCls}>{message.memberName}</span>
            )}
            {isAgent && (
              <Badge tone="agent" size="sm">
                Agent
              </Badge>
            )}
            {variant === 'customAgent' && (
              <Badge tone="agent" variant="subtle" size="sm">
                Custom agent
              </Badge>
            )}
            {message.tag && (
              <>
                <span className={separatorCls} aria-hidden="true">
                  ·
                </span>
                <GovernanceTag domain={message.tag} size="sm" />
              </>
            )}
            {variant === 'mira' && message.trustLevel && (
              <>
                <span className={separatorCls} aria-hidden="true">
                  ·
                </span>
                <TrustBadge level={message.trustLevel} size="sm" />
              </>
            )}
            {message.memberRole && message.memberRole !== message.memberName && (
              <span className={authorRoleCls}>{message.memberRole}</span>
            )}
          </div>
        )}

        {message.agentSignal && (
          <Badge tone="agent" variant="subtle" size="sm">
            {message.agentSignal}
          </Badge>
        )}

        {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- click only reveals the time, which the article's accessible name already carries; a tab stop per message would swamp keyboard users */}
        <div
          className={bubbleClassName}
          onClick={() => {
            // A drag to select text also ends in a click — don't toggle on it.
            if (window.getSelection()?.toString()) return;
            setShowTime((v) => !v);
          }}
        >
          {content}
          {variant === 'agent' && (
            <span className={watermarkCls} aria-hidden="true">
              AGENT · {message.memberName}
            </span>
          )}
        </div>
        {showTime && <span className={timestampCls}>{message.time}</span>}
      </div>
    </article>
  );
}
MessageBubble.displayName = 'MessageBubble';
